"use client";

import { useState, useRef, useEffect } from "react";
import {
  Send,
  HelpCircle,
  User,
  Loader2,
  Map,
  Video,
  BookOpen,
  MessagesSquare,
  ExternalLink,
  ChevronLeft,
  Users,
  UserCircle,
  Bell,
  GraduationCap,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/auth-context";
import { LoginRequired } from "@/components/login-required";
import { STUDY_BUDDY_ENABLED } from "@/constants/feature-flags";

interface Message {
  id: string;
  content: string;
  sender: "user" | "bot";
  timestamp: Date;
  isLynxRedirect?: boolean;
}

interface ExamChatbotProps {
  className?: string;
  fullScreen?: boolean;
  onClose?: () => void;
}

// ── Two-tier quick-action structure ──────────────────────────────────────────

interface SubAction {
  label: string;
  text: string;
}

interface Category {
  id: string;
  label: string;
  icon: React.ElementType;
  actions: SubAction[];
}

const CATEGORIES: Category[] = [
  {
    id: "video",
    label: "Video Studio",
    icon: Video,
    actions: [
      { label: "Create & publish",        text: "How do I create and publish a video?" },
      { label: "Add music",               text: "How do I add music or custom audio to my video?" },
      { label: "Add text & stickers",     text: "How do I add text overlays or stickers to my video?" },
      { label: "Record in-app",           text: "How do I record a video directly inside the app?" },
      { label: "Save as draft",           text: "How do I save a video as a draft?" },
      { label: "My Videos library",       text: "Where can I find all my uploaded videos?" },
    ],
  },
  {
    id: "forums",
    label: "Forums",
    icon: MessagesSquare,
    actions: [
      { label: "Post a topic",            text: "How do I create a new forum topic?" },
      { label: "Comment on a post",       text: "How do I comment or reply to a forum post?" },
      { label: "Delete my post",          text: "How do I delete one of my forum posts?" },
      { label: "Edit my post",            text: "How do I edit a forum post I already published?" },
      { label: "React to a post",         text: "How do I react or like a forum post?" },
      { label: "Report a post",           text: "How do I report an inappropriate forum post?" },
    ],
  },
  {
    id: "navigate",
    label: "Find a feature",
    icon: Map,
    actions: [
      { label: "Study Schedule",          text: "Where do I find my Study Schedule?" },
      { label: "Goals",                   text: "Where can I set and track my certification goals?" },
      { label: "Tutors",                  text: "How do I find and book a tutor?" },
      { label: "Notifications",           text: "Where are my notifications and how do I manage them?" },
      { label: "Messages",                text: "Where is the direct messaging feature?" },
      { label: "Settings",                text: "How do I get to my account settings?" },
    ],
  },
  {
    id: "journey",
    label: "My Journey",
    icon: BookOpen,
    actions: [
      { label: "Update milestones",       text: "How do I update my journey milestones?" },
      { label: "Add a milestone",         text: "How do I add a custom milestone to my journey?" },
      { label: "Delete a milestone",      text: "How do I delete a milestone?" },
      { label: "Track progress",          text: "How do I track my certification progress?" },
      { label: "Change journey phase",    text: "How do I change my current journey phase?" },
    ],
  },
  {
    id: "profile",
    label: "Profile",
    icon: UserCircle,
    actions: [
      { label: "Edit profile",            text: "How do I edit my public profile?" },
      { label: "Change photo",            text: "How do I update my profile picture?" },
      { label: "Update certification",    text: "How do I update my certification information?" },
      { label: "Privacy settings",        text: "How do I change my privacy settings?" },
      { label: "Delete account",          text: "How do I delete my account?" },
    ],
  },
  {
    id: "groups",
    label: "Study Groups",
    icon: Users,
    actions: [
      { label: "Find a group",            text: "How do I find and join a study group?" },
      { label: "Create a group",          text: "How do I create a study group?" },
      { label: "Schedule a meeting",      text: "How do I schedule a meeting in my study group?" },
      { label: "Add resources",           text: "How do I add resources to a study group?" },
      { label: "Leave a group",           text: "How do I leave a study group?" },
    ],
  },
];

export function ExamChatbot({ className = "", fullScreen = false, onClose }: ExamChatbotProps) {
  const { user, isAuthenticated } = useAuth();
  const isStudent = (user as any)?.role === "student" || (user as any)?.roles?.includes("student");

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content: `Welcome! I can help you with billing, technical issues, or general questions. Please tell me what you're looking for.`,
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState<Array<{ role: "user" | "assistant"; content: string }>>([]);
  // null = show categories; string = show sub-actions for that category id
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Sentinel div at the bottom of the message list — scrollIntoView is more
  // reliable than scrollTop on the shadcn ScrollArea viewport.
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      content: trimmed,
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue("");
    setIsLoading(true);
    // Collapse sub-actions after sending
    setActiveCategory(null);

    const updatedHistory: Array<{ role: "user" | "assistant"; content: string }> = [
      ...history,
      { role: "user", content: trimmed },
    ];

    try {
      const res = await fetch("/api/ai/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: trimmed, history }),
      });

      const data = await res.json();

      if (data.redirect_to_lynx) {
        // While the Study Buddy (Lynx) feature is hidden, don't point users to
        // it — fall back to a generic "out of scope" reply with no Lynx CTA.
        const botMsg: Message = {
          id: (Date.now() + 1).toString(),
          content: !STUDY_BUDDY_ENABLED
            ? "Study and exam questions are outside my scope — I'm here to help with app navigation. For study help, try booking a tutor or joining a study group."
            : isStudent
            ? "That sounds like a study question — Lynx is your go-to for that! Lynx is your dedicated AI study companion with live session support."
            : "Study and exam questions are outside my scope — I'm here to help with app navigation only. If you're a student, Lynx (your AI study companion) handles those.",
          sender: "bot",
          timestamp: new Date(),
          isLynxRedirect: STUDY_BUDDY_ENABLED && isStudent,
        };
        setMessages((prev) => [...prev, botMsg]);
        setHistory([...updatedHistory, { role: "assistant", content: botMsg.content }]);
      } else if (data.reply) {
        const botMsg: Message = {
          id: (Date.now() + 1).toString(),
          content: data.reply,
          sender: "bot",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMsg]);
        setHistory([...updatedHistory, { role: "assistant", content: data.reply }]);
      } else {
        const errMsg = "Something went wrong. Please try again.";
        setMessages((prev) => [
          ...prev,
          { id: (Date.now() + 1).toString(), content: errMsg, sender: "bot", timestamp: new Date() },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          content: "Connection error. Please check your internet and try again.",
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <LoginRequired
        title="Lynx Support"
        description="Log in to get help navigating the app"
      />
    );
  }

  const currentCategory = CATEGORIES.find((c) => c.id === activeCategory) ?? null;

  return (
    <Card
      className={`${className} ${fullScreen ? "h-[calc(100dvh-2rem)]" : "h-[min(560px,calc(100dvh-12rem))]"} flex flex-col`}
    >
      {/* Header */}
      <CardHeader className="flex-shrink-0 pb-2 pt-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <div className="h-7 w-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
            <HelpCircle className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">Lynx Support</p>
            <p className="text-[10px] font-normal text-gray-500 leading-tight">App help &amp; navigation</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="ml-auto flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Close support chat"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-2 p-3 min-h-0">
        {/* Messages */}
        <ScrollArea className="flex-1 pr-2">
          <div className="space-y-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-2 ${message.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                {message.sender === "bot" && (
                  <div className="h-7 w-7 flex-shrink-0 mt-0.5" />
                )}

                <div className="max-w-[82%] space-y-1.5">
                  <div
                    className={`rounded-xl px-3 py-2 text-sm ${
                      message.sender === "user"
                        ? "bg-blue-600 text-white ml-auto"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    <p>{message.content}</p>
                  </div>

                  {/* Lynx redirect CTA — only shown to students */}
                  {message.isLynxRedirect && (
                    <a
                      href="/student"
                      className="flex items-center gap-1.5 text-xs text-blue-600 font-medium bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 hover:bg-blue-100 transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                      Open Lynx from your dashboard
                    </a>
                  )}
                </div>

                {message.sender === "user" && (
                  <Avatar className="h-7 w-7 flex-shrink-0 mt-0.5">
                    <AvatarImage src={(user as any)?.image || "/placeholder.svg"} />
                    <AvatarFallback className="text-[10px]">
                      <User className="h-3.5 w-3.5" />
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className="h-7 w-7 flex-shrink-0" />
                <div className="bg-gray-100 rounded-xl px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-500" />
                    <span className="text-xs text-gray-500">Thinking…</span>
                  </div>
                </div>
              </div>
            )}

            {/* Scroll sentinel */}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        {/* Quick actions — two-tier: categories then sub-questions */}
        <div className="flex-shrink-0 space-y-1.5">
          {currentCategory ? (
            /* Sub-actions for selected category */
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setActiveCategory(null)}
                  className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  <ChevronLeft className="h-3 w-3" />
                  Back
                </button>
                <span className="text-[11px] text-gray-400">·</span>
                <span className="text-[11px] text-gray-500 font-medium">{currentCategory.label}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {currentCategory.actions.map(({ label, text }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => sendMessage(text)}
                    disabled={isLoading}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-40"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Category chips */
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveCategory(id)}
                  disabled={isLoading}
                  className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-gray-200 bg-white text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors disabled:opacity-40"
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Input */}
        <div className="flex-shrink-0 flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage(inputValue)}
            placeholder="Ask how to use the app…"
            disabled={isLoading}
            className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 bg-white"
          />
          <Button
            type="button"
            onClick={() => sendMessage(inputValue)}
            disabled={!inputValue.trim() || isLoading}
            size="icon"
            className="h-9 w-9 rounded-xl bg-blue-600 hover:bg-blue-700 flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
